import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal, Eyebrow, MaskLines } from "@/components/site/Reveal";
import { LogoMark } from "@/components/site/Logo";
import { Connect } from "@/components/site/Connect";
import { Seo } from "@/components/site/Seo";

const P = ({ children, className = "" }) => (
  <Reveal y={24}>
    <p className={`text-lg sm:text-xl leading-[1.75] text-zinc-300 ${className}`}>{children}</p>
  </Reveal>
);

const Divider = ({ n }) => (
  <div className="flex items-center gap-4 my-16 sm:my-20" aria-hidden="true">
    <span className="font-mono text-[12px] text-lime">{n}</span>
    <span className="h-px flex-1 bg-white/10" />
    <LogoMark className="w-5 h-5 text-lime/90" />
  </div>
);

const questions = [
  "Where did we come from?",
  "Why are we here?",
  "What is consciousness?",
  "Are we alone?",
  "What does it mean to exist in a universe billions of years old, unimaginably large, and largely indifferent to our existence?",
];

const Portrait = () => (
  <motion.figure
    data-testid="people-portrait"
    initial={{ opacity: 0, y: 30, scale: 0.96 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
    className="group order-1 md:order-2 relative w-20 sm:w-24 md:w-28 aspect-[4/5] rounded-2xl overflow-hidden border border-white/15 shadow-[0_20px_50px_-20px_rgba(200,244,90,0.35)]"
  >
    <img src="/img/apoorva.jpg" alt="Dr Apoorva Chandra" className="w-full h-full object-cover object-top grayscale contrast-125 group-hover:grayscale-0 group-hover:contrast-100 group-hover:scale-105 transition-[filter,transform] duration-700" />
    <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-2xl" />
  </motion.figure>
);

const Hero = () => (
  <section className="relative px-5 sm:px-10 lg:px-16 pt-36 pb-16 overflow-hidden">
    <div className="absolute inset-0 grid-lines opacity-40" />
    <div className="absolute -right-40 top-10 w-[520px] h-[520px] rounded-full bg-lime/10 blur-[120px]" />
    <div className="relative max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-[auto_1fr] gap-10 md:gap-10 items-end">
      <div className="order-2 md:order-1">
      <Link data-testid="back-to-people" to="/people" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.25em] text-zinc-300 hover:text-lime transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> The people behind this page
      </Link>
      <Eyebrow className="mb-6">// Founder - Desi Science Page</Eyebrow>
      <h1 data-testid="people-title" className="font-display font-bold uppercase leading-[0.88] text-5xl sm:text-7xl lg:text-8xl">
        <MaskLines onLoad lines={["Dr Apoorva", <span className="text-lime">Chandra</span>]} />
      </h1>
      </div>
      <Portrait />
      <Reveal delay={0.4} y={20} className="order-3 md:col-span-2">
        <p data-testid="people-intro" className="max-w-3xl font-serif text-2xl sm:text-3xl leading-snug text-zinc-100">
          Hi, I'm Apoorva Chandra — an Emergency Medicine doctor by profession, a science enthusiast by obsession, and an incurably curious human by nature.
        </p>
      </Reveal>
    </div>
  </section>
);

const Questions = () => (
  <Reveal y={24}>
    <ul data-testid="people-questions" className="my-10 border-l-2 border-lime pl-6 space-y-3">
      {questions.map((q) => (
        <li key={q} className="font-display text-xl sm:text-2xl leading-snug text-white">{q}</li>
      ))}
    </ul>
  </Reveal>
);

const Highlight = ({ children, testid }) => (
  <Reveal y={24}>
    <p data-testid={testid} className="my-10 rounded-3xl bg-panel border border-white/10 p-7 sm:p-10 font-serif text-2xl sm:text-3xl leading-snug text-white">
      {children}
    </p>
  </Reveal>
);

export default function People() {
  return (
    <main data-testid="people-page">
      <Seo
        title="Dr Apoorva Chandra — The People Behind | The Desi Science Page"
        description="Apoorva Chandra is an Emergency Medicine doctor, science enthusiast and the curious mind behind The Desi Science Page. Welcome to my rabbit holes."
        path="/people/apoorva-chandra"
      />
      <Hero />
      <article className="px-5 sm:px-10 lg:px-16 pb-24">
        <div className="max-w-3xl mx-auto">
          <Divider n="01" />
          <div className="space-y-7">
            <P>I've always felt that one of the most fascinating things we can do as human beings is try to understand our place in this incomprehensibly vast universe.</P>
          </div>
          <Questions />
          <div className="space-y-7">
            <P>I find something profoundly beautiful about the fact that we are tiny specks of cosmic dust, equipped with just enough intelligence and curiosity to look up at the stars and wonder about it all. To me, few pursuits are more exciting than chasing answers to these questions, even if some of them turn out to be beyond our reach.</P>
            <P className="text-white font-semibold">But I believe there's something equally important in how we search for those answers.</P>
          </div>

          <Divider n="02" />
          <div className="space-y-7">
            <P>Science, to me, is much more than a collection of facts, discoveries or complicated equations. <strong className="text-lime font-semibold">It is a way of thinking.</strong> Perhaps the most powerful tool we have devised to make sense of reality. Not because it is infallible — it absolutely isn't — but because it is designed to recognise its own fallibility. It questions, tests, challenges, revises and, when necessary, overturns its own conclusions.</P>
            <P>It is an imperfect but remarkably self-correcting process, continually evolving and refining itself in pursuit of something we may never fully grasp: the truth. I can't think of a more reliable, self-aware or less error-prone framework we've developed for distinguishing what we believe from what we actually have reason to believe.</P>
          </div>
          <Highlight testid="people-highlight">
            And perhaps that's the real beauty of science: it doesn't demand that we have all the answers. <span className="text-lime">It demands that we're willing to change our minds when better answers come along.</span>
          </Highlight>

          <Divider n="03" />
          <div className="space-y-7">
            <P>Desi Science Page is, in many ways, an extension of that curiosity. It's a collection of subjects close to my heart, ideas that have made me pause, questions that refuse to leave me alone, and a repository of the many rabbit holes I've fallen into, crawled out of, and occasionally remained trapped in.</P>
            <P>You'll find a little bit of everything here — science, medicine, technology, philosophy, the human mind, the universe and the wonderfully bizarre things in between.</P>
            <P className="text-white font-semibold">I'm not here to pretend I have all the answers. Quite the opposite. I'm here because I have far too many questions.</P>
            <P>If there's one thing I hope this space inspires, it's a little more curiosity, a little more scepticism, a willingness to question our assumptions, and an appreciation for the beauty of not knowing.</P>
            <P className="italic text-zinc-400">And perhaps a few existential crises along the way.</P>
            <P>After all, we have this one extraordinary opportunity to exist, on this tiny planet, in this ridiculously enormous universe. We might as well try to understand it.</P>
          </div>
          <Reveal y={30}>
            <p data-testid="people-signoff" className="mt-16 font-hand text-5xl sm:text-7xl leading-tight text-lime">
              Welcome to my rabbit holes. <span className="text-white">Make yourself at home.</span>
            </p>
          </Reveal>
        </div>
      </article>
      <Connect />
    </main>
  );
}
